import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";
import { BRAND } from "@/components/site/data";

const lines = ["Grow wealth", "with quiet", "conviction."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.15 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative h-screen min-h-[680px] w-full overflow-hidden flex items-end bg-[#f6f2eb]"
      data-testid="hero-section"
    >
      {/* Soft layered light background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#f6f2eb] to-[#efe7d8]" />
        <motion.div
          style={{ y: glowY }}
          className="absolute -top-40 right-[-10%] w-[70vw] h-[70vw] rounded-full blur-[120px] opacity-40"
          aria-hidden="true"
        >
          <div className="w-full h-full rounded-full bg-[radial-gradient(circle,#c6a87c_0%,transparent_60%)]" />
        </motion.div>
        <motion.div
          style={{ y: glowY }}
          className="absolute bottom-[-20%] left-[-10%] w-[55vw] h-[55vw] rounded-full blur-[120px] opacity-25"
          aria-hidden="true"
        >
          <div className="w-full h-full rounded-full bg-[radial-gradient(circle,#0e5b45_0%,transparent_60%)]" />
        </motion.div>
      </div>

      {/* Giant faded serif watermark */}
      <motion.span
        style={{ y: glowY }}
        aria-hidden="true"
        className="pointer-events-none select-none absolute -right-6 top-24 font-serif text-[26vw] leading-none text-[#1a1a17]/[0.04] hidden md:block"
      >
        SIP
      </motion.span>

      <motion.div
        style={{ y: contentY, opacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="overline mb-8 flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="w-8 h-px bg-[#b8925a] inline-block" />
          {BRAND.arn} · AMFI Registered
        </motion.p>

        <h1 className="font-serif text-[#1a1a17] text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
              >
                {i === 2 ? (
                  <span className="italic text-[#0e5b45]">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end gap-8 md:justify-between"
        >
          <p className="text-stone-600 text-base md:text-lg max-w-md leading-relaxed">
            I&apos;m {BRAND.name} — helping families across India build lasting wealth
            through disciplined, goal-based mutual fund investing.
          </p>
          <a
            href="#contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 text-[#1a1a17] hover:text-[#0e5b45] transition-colors duration-300 shrink-0"
          >
            <span className="text-sm uppercase tracking-widest border-b border-[#b8925a] pb-1">
              Start your plan
            </span>
            <ArrowDownRight
              size={20}
              className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-300"
            />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
